import { axiosWithConfig } from '@assembly/config/axios'

export type InvoiceStatus =
    | 'DRAFT'
    | 'PENDING'
    | 'SENT'
    | 'PAID'
    | 'PARTIALLY_PAID'
    | 'OVERDUE'
    | 'CANCELLED'
    | 'REFUNDED'
    | 'ERROR'

export type InvoiceType =
    | 'ONE_TIME'
    | 'MONTHLY'
    | 'PRORATED'
    | 'CLOSING'
    | 'ADJUSTMENT'
    | 'INTERNAL'

export type PaymentTerms =
    | 'NET_0'
    | 'NET_7'
    | 'NET_15'
    | 'NET_30'
    | 'NET_60'
    | 'NET_90'

export type LineItemType =
    | 'SUBSCRIPTION'
    | 'CONTRACTOR_HOURS'
    | 'SETUP_FEE'
    | 'OVERAGE'
    | 'ADJUSTMENT'
    | 'DISCOUNT'
    | 'TAX'
    | 'OTHER'

export type PaymentMethod =
    | 'STRIPE'
    | 'BANK_TRANSFER'
    | 'CHECK'
    | 'CASH'
    | 'CREDIT_CARD'
    | 'ACH'
    | 'WIRE'
    | 'OTHER'

export type PaymentStatus =
    | 'PENDING'
    | 'COMPLETED'
    | 'FAILED'
    | 'REFUNDED'
    | 'CANCELLED'

export interface InvoiceLineItem {
    id?: string
    type?: LineItemType
    description: string
    resourceId?: string
    quantity: number
    unit?: string
    unitPrice: number
    totalAmount: number
    billingStart?: string
    billingEnd?: string
    notes?: string
}

export interface InvoiceStatusChange {
    id?: string
    fromStatus?: InvoiceStatus
    toStatus: InvoiceStatus
    timestamp?: number
    changedBy?: string
    reason?: string
    notes?: string
}

export interface InvoicePayment {
    id?: string
    amount: number
    currency?: string
    status?: PaymentStatus
    method: PaymentMethod
    paymentDate?: number
    transactionId?: string
    reference?: string
    notes?: string
    processedBy?: string
    createdDate?: number
    lastUpdated?: number
}

export interface OrganizationSummary {
    id: string
    name: string
    email?: string
}

export interface AssemblyLineSummary {
    id: string
    name: string
    projectType?: string
    status?: string
}

export interface InvoiceCreateRequest {
    organizationId: string
    assemblyLineId: string
    type?: InvoiceType
    paymentTerms?: PaymentTerms
    issueDate?: number
    billingPeriodStart?: string
    billingPeriodEnd?: string
    description?: string
    notes?: string
    lineItems?: InvoiceLineItem[]
    taxAmount?: number
    metadata?: Record<string, string>
}

export interface InvoiceUpdateRequest {
    organizationId?: string
    assemblyLineId?: string
    type?: InvoiceType
    status?: InvoiceStatus
    paymentTerms?: PaymentTerms
    issueDate?: number
    billingPeriodStart?: string
    billingPeriodEnd?: string
    description?: string
    notes?: string
    lineItems?: InvoiceLineItem[]
    subtotalAmount?: number
    taxAmount?: number
    totalAmount?: number
    metadata?: Record<string, string>
}

export interface InvoicePaymentRequest {
    amount: number
    currency?: string
    method: PaymentMethod
    paymentDate?: number
    transactionId?: string
    reference?: string
    notes?: string
    processedBy?: string
}

export interface InvoiceResponse {
    id: string
    invoiceNumber: string
    organization: OrganizationSummary
    assemblyLine: AssemblyLineSummary
    type: InvoiceType
    status: InvoiceStatus
    statusMessage?: string
    paymentTerms: PaymentTerms
    subtotalAmount: number
    taxAmount: number
    totalAmount: number
    paidAmount: number
    remainingAmount: number
    currency: string
    createdDate: number
    issueDate: number
    dueDate: number
    paidDate?: number
    lastUpdated: number
    billingPeriodStart: string
    billingPeriodEnd: string
    lineItems: InvoiceLineItem[]
    stripeInvoiceId?: string
    stripeCustomerId?: string
    externalReference?: string
    description?: string
    notes?: string
    metadata?: Record<string, string>
    createdBy?: string
    lastModifiedBy?: string
    statusHistory: InvoiceStatusChange[]
    payments: InvoicePayment[]
    isOverdue: boolean
    isFullyPaid: boolean
    daysPastDue: number
}

export interface InvoicePaginatedResponse {
    invoices: InvoiceResponse[]
    totalCount: number
    pageSize: number
    pageNumber: number
    currentPage: number
    totalPages: number
    hasNext: boolean
    hasPrevious: boolean
}

export interface ErrorResponse {
    error: string
    code: string
    timestamp: number
    path: string
}

// API Functions
export const clientInvoicesApi = {
    // Health check
    ping: () => axiosWithConfig.get<string>('/invoices/ping'),

    // Create invoice
    createInvoice: (data: InvoiceCreateRequest) =>
        axiosWithConfig.post<InvoiceResponse>('/invoices', data),

    // Get invoice by ID
    getInvoiceById: (invoiceId: string) =>
        axiosWithConfig.get<InvoiceResponse>(`/invoices/${invoiceId}`),

    // Update invoice
    updateInvoice: (invoiceId: string, data: InvoiceUpdateRequest) =>
        axiosWithConfig.put<InvoiceResponse>(`/invoices/${invoiceId}`, data),

    // Delete invoice
    deleteInvoice: (invoiceId: string) =>
        axiosWithConfig.delete(`/invoices/${invoiceId}`),

    // Get invoices by organization
    getInvoicesByOrganization: (
        orgId: string,
        page: number = 0,
        pageSize: number = 20,
        status?: InvoiceStatus
    ) =>
        axiosWithConfig.get<InvoicePaginatedResponse>(
            `/invoices/organization/${orgId}?page=${page}&pageSize=${pageSize}${status ? `&status=${status}` : ''
            }`
        ),

    // Get invoices by assembly line
    getInvoicesByAssemblyLine: (
        assemblyLineId: string,
        page: number = 0,
        pageSize: number = 20,
        status?: InvoiceStatus
    ) =>
        axiosWithConfig.get<InvoicePaginatedResponse>(
            `/invoices/assembly-line/${assemblyLineId}?page=${page}&pageSize=${pageSize}${status ? `&status=${status}` : ''
            }`
        ),

    // Get overdue invoices
    getOverdueInvoices: (page: number = 0, pageSize: number = 20) =>
        axiosWithConfig.get<InvoicePaginatedResponse>(
            `/invoices/overdue?page=${page}&pageSize=${pageSize}`
        ),

    // Get invoices by status
    getInvoicesByStatus: (
        status: InvoiceStatus,
        page: number = 0,
        pageSize: number = 20
    ) =>
        axiosWithConfig.get<InvoicePaginatedResponse>(
            `/invoices/status/${status}?page=${page}&pageSize=${pageSize}`
        ),

    // Update invoice status
    updateInvoiceStatus: (
        invoiceId: string,
        status: InvoiceStatus,
        reason?: string,
        changedBy?: string
    ) =>
        axiosWithConfig.put<InvoiceResponse>(
            `/invoices/${invoiceId}/status?status=${status}${reason ? `&reason=${encodeURIComponent(reason)}` : ''
            }${changedBy ? `&changedBy=${encodeURIComponent(changedBy)}` : ''}`
        ),

    // Add payment to invoice
    addPaymentToInvoice: (invoiceId: string, data: InvoicePaymentRequest) =>
        axiosWithConfig.post<InvoiceResponse>(`/invoices/${invoiceId}/payments`, data),

    // Export invoice as PDF
    exportInvoicePDF: (invoiceId: string) =>
        axiosWithConfig.get(`/invoices/${invoiceId}/export/pdf`, {
            responseType: 'blob',
        }),

    // Search invoices with advanced filtering
    searchInvoices: (
        params: {
            searchText?: string
            organizationId?: string
            assemblyLineId?: string
            status?: InvoiceStatus
            type?: InvoiceType
            paymentTerms?: PaymentTerms
            dateFrom?: number
            dateTo?: number
            billingPeriodStart?: string
            billingPeriodEnd?: string
            minAmount?: number
            maxAmount?: number
            currency?: string
            page?: number
            pageSize?: number
        }
    ) => {
        const queryParams = new URLSearchParams()

        if (params.searchText) queryParams.append('q', params.searchText)
        if (params.organizationId) queryParams.append('organizationId', params.organizationId)
        if (params.assemblyLineId) queryParams.append('assemblyLineId', params.assemblyLineId)
        if (params.status) queryParams.append('status', params.status.toString())
        if (params.type) queryParams.append('type', params.type.toString())
        if (params.paymentTerms) queryParams.append('paymentTerms', params.paymentTerms.toString())
        if (params.dateFrom) queryParams.append('dateFrom', params.dateFrom.toString())
        if (params.dateTo) queryParams.append('dateTo', params.dateTo.toString())
        if (params.billingPeriodStart) queryParams.append('billingPeriodStart', params.billingPeriodStart)
        if (params.billingPeriodEnd) queryParams.append('billingPeriodEnd', params.billingPeriodEnd)
        if (params.minAmount !== undefined) queryParams.append('minAmount', params.minAmount.toString())
        if (params.maxAmount !== undefined) queryParams.append('maxAmount', params.maxAmount.toString())
        if (params.currency) queryParams.append('currency', params.currency)
        if (params.page !== undefined) queryParams.append('page', params.page.toString())
        if (params.pageSize !== undefined) queryParams.append('pageSize', params.pageSize.toString())

        return axiosWithConfig.get<InvoicePaginatedResponse>(`/invoices/search?${queryParams.toString()}`)
    },

    // Send invoice email
    sendInvoiceEmail: (
        invoiceId: string,
        data: {
            toAddresses: string[]
            ccAddresses?: string[]
            additionalNote?: string
            attachment?: File
        }
    ) => {
        const formData = new FormData()

        // Convert arrays to comma-separated strings as expected by the backend
        formData.append('toAddresses', data.toAddresses.join(','))
        if (data.ccAddresses && data.ccAddresses.length > 0) {
            formData.append('ccAddresses', data.ccAddresses.join(','))
        }
        if (data.additionalNote) {
            formData.append('additionalNote', data.additionalNote)
        }
        if (data.attachment) {
            formData.append('attachment', data.attachment)
        }

        // Let axios/browser handle Content-Type automatically for multipart/form-data
        return axiosWithConfig.post<string>(`/invoices/${invoiceId}/send-email`, formData)
    },
}
